/**
 * Error Handler - Sistema robusto de manejo de errores del backend
 * Parsea múltiples estructuras de respuesta y normaliza el output
 */

/**
 * Parser robusto de errores del backend
 * Maneja múltiples estructuras de respuesta y normaliza el output
 *
 * @param {Error} error - Error object de axios
 * @returns {Object} Objeto normalizado con userMessage, type, etc.
 */
export const parseBackendError = (error) => {
  // Nivel 1: Sin respuesta del servidor (network error, timeout, etc.)
  if (!error.response) {
    return {
      userMessage:
        "No pudimos conectar con el servidor. Por favor, verificá tu conexión a internet.",
      technicalDetails: error.message || "Network Error",
      type: "NETWORK_ERROR",
    };
  }

  const { status, data } = error.response;

  // Nivel 2: Errores HTTP específicos sin data
  if (!data) {
    return getHttpStatusMessage(status);
  }

  // Nivel 3: Parsear diferentes estructuras de data del backend
  return parseErrorData(data, status);
};

/**
 * Parsea diferentes estructuras de errores que puede enviar el backend
 *
 * @param {*} data - Data del error response
 * @param {number} status - HTTP status code
 * @returns {Object} Objeto normalizado
 */
const parseErrorData = (data, status) => {
  // Caso 1: Errores de validación con array (ASP.NET ValidationProblemDetails)
  if (data.errors && typeof data.errors === "object") {
    const validationMessages = extractValidationMessages(data.errors);

    return {
      userMessage: data.title || "Hay errores en el formulario",
      validationErrors: validationMessages,
      technicalDetails: data.detail || JSON.stringify(data.errors),
      type: "VALIDATION_ERROR",
      rawData: data,
    };
  }

  // Caso 2: Error con detail y title separados
  if (data.detail && data.title) {
    return {
      userMessage: data.detail,
      title: data.title,
      technicalDetails: data.traceId || data.detail,
      type: "BUSINESS_ERROR",
      rawData: data,
    };
  }

  // Caso 3: Error simple con mensaje directo (string)
  if (typeof data === "string") {
    return {
      userMessage: data,
      technicalDetails: data,
      type: "STRING_ERROR",
    };
  }

  // Caso 4: Error con propiedad "message" o "mensaje"
  if (data.message || data.mensaje) {
    return {
      userMessage: data.message || data.mensaje,
      technicalDetails: JSON.stringify(data),
      type: "MESSAGE_ERROR",
      rawData: data,
    };
  }

  // Caso 5: Error con propiedad "detail" solamente
  if (data.detail) {
    return {
      userMessage: data.detail,
      technicalDetails: data.detail,
      type: "DETAIL_ERROR",
      rawData: data,
    };
  }

  // Fallback final: No pudimos parsear nada conocido
  return {
    userMessage:
      "Lo sentimos, ocurrió un error inesperado. Por favor, intentá nuevamente.",
    technicalDetails: JSON.stringify(data),
    type: "UNKNOWN_ERROR",
    rawData: data,
  };
};

/**
 * Extrae mensajes de errores de validación del formato ASP.NET
 *
 * @param {Object|Array} errors - Objeto o array de errores de validación
 * @returns {Array} Array de objetos {field, message}
 */
const extractValidationMessages = (errors) => {
  const messages = [];

  // Si errors es un array
  if (Array.isArray(errors)) {
    errors.forEach((error) => {
      if (error.PropertyName && error.ErrorMessage) {
        messages.push({
          field: error.PropertyName,
          message: error.ErrorMessage,
        });
      }
    });
  }
  // Si errors es un objeto con propiedades
  else if (typeof errors === "object") {
    Object.keys(errors).forEach((key) => {
      const errorValue = errors[key];

      // Puede ser un array de strings
      if (Array.isArray(errorValue)) {
        errorValue.forEach((msg) => {
          messages.push({
            field: key,
            message: msg,
          });
        });
      }
      // O un string directo
      else if (typeof errorValue === "string") {
        messages.push({
          field: key,
          message: errorValue,
        });
      }
    });
  }

  return messages;
};

/**
 * Mensajes por código HTTP cuando no hay data específica
 *
 * @param {number} status - HTTP status code
 * @returns {Object} Objeto normalizado
 */
const getHttpStatusMessage = (status) => {
  const statusMessages = {
    400: {
      userMessage:
        "Los datos enviados no son válidos. Por favor, revisá el formulario.",
      type: "BAD_REQUEST",
    },
    401: {
      userMessage: "Tu sesión expiró. Por favor, ingresá nuevamente.",
      type: "UNAUTHORIZED",
    },
    403: {
      userMessage: "No tenés permisos para realizar esta acción.",
      type: "FORBIDDEN",
    },
    404: {
      userMessage: "No encontramos el recurso solicitado.",
      type: "NOT_FOUND",
    },
    409: {
      userMessage: "Ya existe un registro con estos datos.",
      type: "CONFLICT",
    },
    422: {
      userMessage:
        "Los datos no pudieron ser procesados. Verificá la información ingresada.",
      type: "UNPROCESSABLE_ENTITY",
    },
    500: {
      userMessage:
        "Error en el servidor. Por favor, intentá nuevamente más tarde.",
      type: "SERVER_ERROR",
    },
    503: {
      userMessage:
        "El servicio no está disponible temporalmente. Intentá en unos minutos.",
      type: "SERVICE_UNAVAILABLE",
    },
  };

  return (
    statusMessages[status] || {
      userMessage:
        "Lo sentimos, ocurrió un error inesperado. Por favor, intentá nuevamente.",
      technicalDetails: `HTTP ${status}`,
      type: "HTTP_ERROR",
    }
  );
};

/**
 * Formatea mensajes de validación para mostrar al usuario
 *
 * @param {Array} validationErrors - Array de {field, message}
 * @returns {string} Mensaje formateado
 */
export const formatValidationErrorsForUser = (validationErrors) => {
  if (!validationErrors || validationErrors.length === 0) {
    return "";
  }

  if (validationErrors.length === 1) {
    return validationErrors[0].message;
  }

  // Múltiples errores: crear lista
  return validationErrors
    .map((error, index) => `${index + 1}. ${error.field}: ${error.message}`)
    .join("\n");
};

/**
 * Log de errores para debugging (opcional, útil en desarrollo)
 *
 * @param {Error} error - Error object
 * @param {string} context - Contexto del error
 */
export const logError = (error, context = "") => {
  if (process.env.NODE_ENV === "development") {
    console.group(`❌ Error: ${context}`);
    console.error("Original Error:", error);
    console.error("Parsed Error:", parseBackendError(error));
    console.groupEnd();
  }
};

